import type { NotificationDto } from "@eduleo/shared";
import { Bell, BellOff, Settings } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router";
import { Button } from "../../components/ui/button";
import { Alert, Skeleton } from "../../components/ui/feedback";
import { describeError } from "../../lib/api-client";
import { cn } from "../../lib/cn";
import { formatDateTime, timeAgo } from "../../lib/format";
import { useMarkSeen, useNotifications, useUnreadCount } from "./notifications-api";

const SETTINGS = "/platform/settings/notifications";

function countLabel(count: number): string {
  return count > 99 ? "99+" : String(count);
}

/**
 * The console's bell (Eduleo's team only): how many notifications are new, and the latest
 * ones in a panel. Opening the panel marks them as seen. Each one links to where it happened.
 */
export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const container = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const panelId = useId();
  const unread = useUnreadCount(true);
  const feed = useNotifications(open);
  const markSeen = useMarkSeen();
  const count = unread.data?.unreadCount ?? 0;

  // Once the list is on screen, what it shows is no longer new. If new ones arrived after the
  // list was loaded (the bell asked again while it was open), show them before marking.
  const { mutate: seen, isPending: marking } = markSeen;
  const { refetch: reloadFeed, isFetching: loadingFeed, dataUpdatedAt: listedAt } = feed;
  const countedAt = unread.dataUpdatedAt;
  useEffect(() => {
    if (!open || !feed.isSuccess || count === 0 || marking || loadingFeed) return;
    if (listedAt < countedAt) void reloadFeed();
    else seen();
  }, [open, feed.isSuccess, count, marking, loadingFeed, listedAt, countedAt, reloadFeed, seen]);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: PointerEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        button.current?.focus();
      }
    };
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Someone whose account can't have notifications (no team role at all) sees no bell.
  if (unread.isError && !unread.data) return null;

  const items = feed.data?.pages.flatMap((page) => page.items) ?? [];
  const label = count > 0 ? `Notifications, ${countLabel(count)} new` : "Notifications";

  return (
    <div ref={container} className="relative">
      <button
        ref={button}
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={label}
        className="relative grid size-11 cursor-pointer place-items-center rounded-full text-ink-2 hover:bg-surface-2 hover:text-ink"
      >
        <Bell aria-hidden className="size-5" strokeWidth={1.5} />
        {count > 0 && (
          <span
            aria-hidden
            className="absolute top-1.5 right-1 min-w-[18px] rounded-full bg-danger px-1 text-center font-bold text-[11px] text-white leading-[18px]"
          >
            {countLabel(count)}
          </span>
        )}
      </button>
      <span className="sr-only" aria-live="polite">
        {count > 0 ? `${countLabel(count)} new notifications` : ""}
      </span>

      <section
        id={panelId}
        hidden={!open}
        aria-label="Notifications"
        className="fixed inset-x-2 top-[68px] z-40 flex max-h-[calc(100dvh-160px)] flex-col overflow-hidden rounded-lg border border-edge bg-surface shadow-lg md:absolute md:inset-x-auto md:top-auto md:right-0 md:mt-2 md:max-h-[70vh] md:w-[400px]"
      >
        <div className="flex items-center justify-between gap-2 border-edge border-b px-4 py-3">
          <h2 className="font-bold">Notifications</h2>
          <Link
            to={SETTINGS}
            onClick={() => setOpen(false)}
            className="inline-flex min-h-11 items-center gap-1.5 font-semibold text-brand-fg text-sm underline-offset-4 hover:underline md:min-h-0"
          >
            <Settings aria-hidden className="size-4" strokeWidth={1.5} />
            Settings
          </Link>
        </div>
        {open && (
          <div className="min-h-0 flex-1 overflow-y-auto">
            {feed.isPending ? (
              <FeedSkeleton />
            ) : feed.isError ? (
              <div className="p-4">
                <Alert
                  tone="danger"
                  title={describeError(feed.error).title}
                  action={
                    <Button variant="secondary" size="sm" onClick={() => feed.refetch()}>
                      Try again
                    </Button>
                  }
                >
                  {describeError(feed.error).detail}
                </Alert>
              </div>
            ) : items.length === 0 ? (
              <div className="flex flex-col items-center gap-2 px-6 py-10 text-center">
                <span className="grid size-12 place-items-center rounded-full bg-surface-2 text-ink-3">
                  <BellOff aria-hidden className="size-6" strokeWidth={1.5} />
                </span>
                <p className="font-semibold">You're all caught up</p>
                <p className="text-ink-3 text-sm">
                  News about your schools, and changes to your own sign-in, will show here.
                </p>
                <Link
                  to={SETTINGS}
                  onClick={() => setOpen(false)}
                  className="font-semibold text-brand-fg text-sm underline-offset-4 hover:underline"
                >
                  Choose what you hear about
                </Link>
              </div>
            ) : (
              <>
                <ul className="divide-y divide-edge">
                  {items.map((item) => (
                    <NotificationItem key={item.id} item={item} onOpen={() => setOpen(false)} />
                  ))}
                </ul>
                {feed.hasNextPage && (
                  <div className="border-edge border-t p-3">
                    <Button
                      variant="ghost"
                      className="w-full"
                      loading={feed.isFetchingNextPage}
                      loadingText="Loading…"
                      onClick={() => feed.fetchNextPage()}
                    >
                      Show older
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </section>
    </div>
  );
}

function FeedSkeleton() {
  return (
    <div className="flex flex-col gap-4 p-4" aria-busy="true">
      <span className="sr-only" role="status">
        Loading…
      </span>
      {[0, 1, 2].map((row) => (
        <div key={row} className="flex flex-col gap-2">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-24" />
        </div>
      ))}
    </div>
  );
}

function NotificationItem({ item, onOpen }: { item: NotificationDto; onOpen: () => void }) {
  const content = (
    <>
      <span className="flex items-start gap-2">
        {item.unread && (
          <span aria-hidden className="mt-1.5 size-2 shrink-0 rounded-full bg-brand" />
        )}
        <span className={cn("min-w-0", item.unread ? "font-bold" : "font-semibold")}>
          {item.unread && <span className="sr-only">New: </span>}
          {item.title}
        </span>
      </span>
      <span className="line-clamp-2 text-ink-2 text-sm">{item.body}</span>
      <time
        dateTime={item.createdAt}
        title={formatDateTime(item.createdAt)}
        className="text-ink-3 text-xs"
      >
        {timeAgo(item.createdAt)}
      </time>
    </>
  );
  const box = cn(
    "flex w-full flex-col gap-1 px-4 py-3 text-left",
    item.unread && "bg-brand-soft/60",
  );
  if (!item.link) return <li className={box}>{content}</li>;
  return (
    <li>
      <Link to={item.link} onClick={onOpen} className={cn(box, "hover:bg-surface-2")}>
        {content}
      </Link>
    </li>
  );
}
